import React, { useMemo } from 'react'
import { Box, Layout, Page, useBreakpoints } from '@shopify/polaris'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { useQuery } from 'react-query'

import AccountSection from './sections/account'
import SettingsSection from './sections/setting'
import IntegrationsSection from './sections/integrations'
import AdvancedConfig from './sections/AdvancedSection'
import EnrichmentSection from './sections/enrichment'
import { useDashboardDetails } from '../../hooks/useDashboardDetails'
import { numberFormater } from '../../utils/formater'
import TemplatesSection from './sections/templates'
import JobsSection from './sections/jobs'
import { getPlans } from '../../apis/plans'
import Recommendation from './sections/recommendations'
import { PricingModal, usePricing } from '../../components/PricingModal'
import DashboardSettings from './sections/dashboard'
import { getQueryParams } from '../../utils/router'
import StorefrontSettings from './sections/storefrontSettings'

const SURPASS_THRESHOLD = 0.8

function Settings() {
  const { t } = useTranslation()

  const { data = {}, dashboardLoading, invalidate } = useDashboardDetails()
  const pricing = usePricing()
  const breakpoints = useBreakpoints()
  const { isLoading: plansLoading, data: plans = [] } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const res = await getPlans()
      if (res.error) {
        return []
      }
      return res.data
    },
  })
  
  // Find the current plan from the list of plans
  const plan = useMemo(() => {
    if (!plans.length || !data?.shopPlan?.plan) return null
    // Find plan that matches the shopPlan.plan id
    return plans.find(p => p.id === data.shopPlan.plan || p.slug === data.shopPlan.plan)
  }, [plans, data?.shopPlan?.plan])
  const { recSection = null } = getQueryParams()

  const settings = useMemo(
    () => ({
      account: {
        monthlyCharge: '$' + data?.shopPlan?.price,
        annualCharge: '$' + data?.shopPlan?.annualPrice,
        additionalUsageCharge: data?.shopPlan?.isExtendedUsageEnabled
          ? '$0.0'
          : null,
        billingPeriod: `${data?.billingCycle?.startedOn && format(new Date(data?.billingCycle?.startedOn), 'dd MMM, yyyy')} - ${data?.billingCycle?.endingOn && format(new Date(data?.billingCycle?.endingOn), 'dd MMM, yyyy')}`,
        widgetsConsumed: parseFloat(
          (
            data?.billingCycle?.currentUsage /
            data?.billingCycle?.recommendationsServedHardLimit
          ).toFixed(2) ?? 0,
        ),
        consumedWidgets: numberFormater(data?.billingCycle?.currentUsage) ?? 0,
        totalWidgets: numberFormater(
          data?.billingCycle?.maximumRecommendationsServed,
        ),
        maxPostWidgets: numberFormater(
          data?.billingCycle?.recommendationsServedHardLimit,
        ),
        upgradeRequired: data?.shopPlan?.price === '0.00',
        planName: plan?.name,
        usageSurpassed:
          data?.billingCycle?.currentUsage >=
          data?.billingCycle?.recommendationsServedHardLimit,
        usageAboutToSurpass:
          data?.billingCycle?.currentUsage /
            data?.billingCycle?.recommendationsServedHardLimit >
            SURPASS_THRESHOLD &&
          data?.billingCycle?.currentUsage <
            data?.billingCycle?.recommendationsServedHardLimit,
        onPaidPlan: parseFloat(data?.shopPlan?.price) !== 0,
      },
      advancedConfig: {
        css: data?.config?.css,
        js: data?.config?.js,
        storeData: JSON.stringify(data?.config?.data, null, 2),
        extra: data?.config?.extra ?? {},
        lastTriggered: data?.config?.productEventsSyncedAt
          ? format(
              new Date(data?.config?.productEventsSyncedAt),
              'dd MMM, yyyy',
            )
          : 'Never',
        wait:
          new Date() - new Date(data?.config?.productEventsSyncedAt) <
          6 * 3600 * 1000, // 6 hours
      },
      enrichment: {
        storeData: data?.config?.data,
      },
      search: {
        storeData: data?.config?.data,
        data: data?.config?.data?.search || {}
      },
      isExtendedUsageEnabled: data?.shopPlan?.isExtendedUsageEnabled,
      onAnnualPlan: data?.billingCycle?.isAnnual,
    }),
    [data, plan],
  )

  return (
    <Page
      title={t('SettingsPage.title')}
      backAction={{
        url: '/',
      }}
      subtitle={t('SettingsPage.description')}
    >
      <Box padding={breakpoints.smUp ? '0' : '400'}>
        <Layout>
          <AccountSection
            pricing={pricing}
            shopPlan={{
              ...(data?.shopPlan ?? {}),
              planDetails: plan,
            }}
            billingCycle={data?.billingCycle ?? {}}
            config={settings.account}
            dashboardData={data}
            loading={dashboardLoading || plansLoading}
          />
          <StorefrontSettings
            dashboardData={data}
            dashboardLoading={dashboardLoading}
            invalidateDashboard={invalidate}
          />
          <Recommendation selectedTab={recSection} />
          <SettingsSection />
          <TemplatesSection dashboardData={data} />
          <JobsSection />
          <EnrichmentSection
            config={settings.enrichment}
            loading={dashboardLoading}
          />
          <IntegrationsSection />
          <AdvancedConfig
            pricing={pricing}
            config={settings.advancedConfig}
            loading={dashboardLoading || plansLoading}
            dashboardData={data}
          />
          <DashboardSettings />
          <Box padding={'400'} />
        </Layout>
      </Box>
      <PricingModal modal={pricing} />
    </Page>
  )
}

export default Settings
